<template>
  <SalesPlayDrawerCard>
    <div class="drawer-section">
      <div class="drawer-sectionLabel">SOQL QUERY</div>
      <textarea
        class="drawer-codeInput"
        v-model="localSoql"
        :placeholder="placeholder"
        rows="5"
        @blur="applySoql"
      />
    </div>

    <div class="drawer-section">
      <button class="btn secondary" type="button" :disabled="!localSoql.trim() || previewing" @click="onPreview">
        {{ previewing ? 'Running...' : 'Preview' }}
      </button>
    </div>

    <div v-if="previewError" class="drawer-section">
      <div class="drawer-errorText">{{ previewError }}</div>
    </div>

    <div v-if="previewRecords.length > 0" class="drawer-section">
      <div class="drawer-sectionLabel">
        PREVIEW
        <span class="drawer-previewMeta">{{ previewTotalSize }} record{{ previewTotalSize !== 1 ? 's' : '' }}{{ previewTruncated ? '+' : '' }}</span>
      </div>
      <div class="drawer-previewTable">
        <table>
          <thead>
            <tr>
              <th v-for="col in previewColumns" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in previewRecords" :key="i">
              <td v-for="col in previewColumns" :key="col">{{ formatCell(row[col]) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </SalesPlayDrawerCard>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SalesPlayDrawerCard from './SalesPlayDrawerCard.vue'
import { apiFetch } from '../../../services/api'
import { useSalesPlayDraft } from '../../composables/useSalesPlayDraft'

const props = defineProps<{
  playId: string
  pipelineId: string | null
  nodeIndex: number
  placeholder?: string
}>()

const { nodes, updateNodeConfig } = useSalesPlayDraft()

const currentConfig = () => nodes.value[props.nodeIndex]?.config || {}

const localSoql = ref(currentConfig().soql || '')

function resetLocal() {
  localSoql.value = currentConfig().soql || ''
  previewRecords.value = []
  previewError.value = ''
}

watch(() => nodes.value[props.nodeIndex]?.config, () => {
  resetLocal()
}, { deep: true })

// ── Preview ─────────────────────────────────────────────────────

const previewing = ref(false)
const previewError = ref('')
const previewRecords = ref<any[]>([])
const previewTotalSize = ref(0)
const previewTruncated = ref(false)

const previewColumns = computed(() => {
  if (previewRecords.value.length === 0) return []
  return Object.keys(previewRecords.value[0])
})

async function onPreview() {
  const soql = localSoql.value.trim()
  if (!soql) return
  previewing.value = true
  previewError.value = ''
  previewRecords.value = []

  try {
    const resp: any = await apiFetch('/integrations/salesforce/soql-preview', {
      method: 'POST',
      body: { soql, max_records: 10 },
    })
    previewRecords.value = resp.records || []
    previewTotalSize.value = resp.total_size || 0
    previewTruncated.value = resp.truncated || false
  } catch (e: any) {
    previewError.value = e?.message || 'Preview failed'
  } finally {
    previewing.value = false
  }
}

function formatCell(value: any): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function applySoql() {
  const soql = localSoql.value.trim()
  if (!soql) return
  updateNodeConfig(props.nodeIndex, { soql })
}
</script>

